import { useSelector } from 'react-redux'
import { useState } from 'react';
import { ResponsiveChartContainer, LinePlot, MarkPlot, ChartsXAxis, ChartsYAxis, ChartsLegend, ChartsGrid, MarkElement } from '@mui/x-charts';
import { Typography, Box, Tooltip, IconButton, Collapse } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import './print.css';
import './mark.css';
// import raceplanner_qr from './raceplanner-qr.png';
// Import icons for POI types
import {
    Bungalow as CampingIcon,
    Hotel as LodgingIcon,
    LocalParking as ParkingIcon,
    Restaurant as FoodIcon,
    Landscape as ViewpointIcon,
    Wc as RestroomIcon,
    Place as GenericIcon,
    LocalHospital as AidStationIcon,
    LocalBar as BarIcon,
    PedalBike as BikeShopIcon,
    DirectionsBike as BikeParkingIcon,
    LocalConvenienceStore as ConvenienceStoreIcon,
    MedicalServices as FirstAidIcon,
    Emergency as HospitalIcon,
    Chair as RestStopIcon,
    Hiking as TrailheadIcon,
    Search as GeocacheIcon,
    Water as WaterIcon,
    Flag as ControlIcon,
    WineBar as WineryIcon,
    PlayArrow as StartIcon,
    Stop as StopIcon,
    SportsScore as FinishIcon,
    LocalAtm as AtmIcon,
    Warning as CautionIcon,
    Coffee as CoffeeIcon,
    DirectionsBoat as FerryIcon,
    LocalGasStation as GasStationIcon,
    LocalLibrary as LibraryIcon,
    AccountBalance as MonumentIcon,
    Park as ParkIcon,
    Timer as SegmentStartIcon,
    TimerOff as SegmentEndIcon,
    LocalMall as ShoppingIcon,
    Shower as ShowerIcon,
    Terrain as SummitIcon,
    Pool as SwimmingIcon,
    Train as TransitCenterIcon,
    ElectricBike as BikeShareIcon,
} from '@mui/icons-material';

// POI type mapping
const POI_TYPE_ICONS = {
    3: { icon: CampingIcon, label: 'Camping' },
    10: { icon: LodgingIcon, label: 'Lodging' },
    12: { icon: ParkingIcon, label: 'Parking' },
    13: { icon: FoodIcon, label: 'Food' },
    14: { icon: ViewpointIcon, label: 'Viewpoint' },
    15: { icon: RestroomIcon, label: 'Restroom' },
    17: { icon: GenericIcon, label: 'Information' },
    20: { icon: AidStationIcon, label: 'Aid Station' },
    21: { icon: BarIcon, label: 'Bar' },
    22: { icon: BikeShopIcon, label: 'Bike Shop' },
    23: { icon: BikeParkingIcon, label: 'Bike Parking' },
    24: { icon: ConvenienceStoreIcon, label: 'Convenience Store' },
    25: { icon: FirstAidIcon, label: 'First Aid' },
    26: { icon: HospitalIcon, label: 'Hospital' },
    27: { icon: RestStopIcon, label: 'Rest Stop' },
    28: { icon: TrailheadIcon, label: 'Trailhead' },
    29: { icon: GeocacheIcon, label: 'Geocache' },
    30: { icon: WaterIcon, label: 'Water' },
    31: { icon: ControlIcon, label: 'Control' },
    32: { icon: WineryIcon, label: 'Winery' },
    33: { icon: StartIcon, label: 'Start' },
    34: { icon: StopIcon, label: 'Stop' },
    35: { icon: FinishIcon, label: 'Finish' },
    36: { icon: AtmIcon, label: 'ATM' },
    37: { icon: CautionIcon, label: 'Caution' },
    38: { icon: CoffeeIcon, label: 'Coffee' },
    39: { icon: FerryIcon, label: 'Ferry' },
    40: { icon: GasStationIcon, label: 'Gas Station' },
    41: { icon: LibraryIcon, label: 'Library' },
    42: { icon: MonumentIcon, label: 'Monument' },
    43: { icon: ParkIcon, label: 'Park' },
    44: { icon: SegmentStartIcon, label: 'Segment Start' },
    45: { icon: SegmentEndIcon, label: 'Segment End' },
    46: { icon: ShoppingIcon, label: 'Shopping' },
    47: { icon: ShowerIcon, label: 'Shower' },
    48: { icon: SummitIcon, label: 'Summit' },
    49: { icon: SwimmingIcon, label: 'Swimming' },
    50: { icon: TransitCenterIcon, label: 'Transit Center' },
    51: { icon: BikeShareIcon, label: 'Bike Share' },
};

// Function to get POI icon component
function PoiIcon({ type, fontSize = 'small', sx = {} }) {
    // console.log('POI type:', type, 'Config:', POI_TYPE_ICONS[type]);
    const iconConfig = POI_TYPE_ICONS[type] || POI_TYPE_ICONS[17];
    const IconComponent = iconConfig.icon;
    return (
        <Tooltip title={iconConfig.label} arrow>
            <IconComponent fontSize={fontSize} sx={sx} />
        </Tooltip>
    );
}

const slotsMark = (props) => {
    // console.log('slotsMark', props)
    const { userIndex, ...restProps } = props;
    return (
        <MarkElement {...restProps} classes={{ root: props.dataIndex === userIndex ? "userMarkElement" : "markElement" }} />
    )
}

// lays out a chart which fills the space of it's parent
function Chart({ chunk, yAxisRange = null }) {
    const chartSettings = useSelector(state => state.chartSettings.chartSettings);
    const userIndex = useSelector(state => state.charts.userLocation?.lastLocation?.userIndex);
    // const { location } = useUserLocation();

    // Apply elevation points density for y-axis ticks only
    const density = chartSettings.elevationPointsDensity;

    return (<ResponsiveChartContainer
        xAxis={[{
            data: chunk.d,
            id: 'x-axis-id',
            min: Math.min(...chunk.d),
            max: Math.max(...chunk.d)
        }]}
        yAxis={[{
            id: 'y-axis-id',
            data: chunk.e,
            min: yAxisRange?.min,
            max: yAxisRange?.max,
            tickNumber: Math.max(3, Math.floor(density * chartSettings.chartHeight * 1.5)) // Reduced tick density relative to height
        }]}
        title="Elevation Profile"
        series={[
            {
                type: "line",
                data: chunk.e,
                showMark: ({ index }) => {
                    const poiName = chunk.n[index];
                    if (!poiName) return false;
                    if (chartSettings.hideOffRoutePoi) {
                        const poi = chunk.poi.find(p => p.n === poiName);
                        if (poi && poi.offRouteDistance > chartSettings.offRouteThreshold) {
                            return false;
                        }
                    }
                    return true;
                },
                fill: false,
                borderColor: "rgb(75, 192, 192)",
                tension: 0.1,
                curve: "linear",
                skipAnimation: true,
                color: "#FF5722",
                shape: "circle"
            },
        ]}>
        {chartSettings.showGrid && (
            <ChartsGrid
                horizontal={true}
                vertical={true}
                strokeDasharray="3 3"
                stroke="#bdbdbd"
            />
        )}
        <MarkPlot
            slots={{
                mark: slotsMark
            }}
            slotProps={{
                mark: {
                    userIndex: userIndex
                }
            }}
            onItemClick={(evt, lineItemId) => {
                const index = lineItemId.dataIndex;
                const name = chunk.n[index];
                // index is the point index
                // chunk.poi has the entire poi, you could find it by name to get all the other details
                console.log(name);
            }} />
        <LinePlot />
        <ChartsXAxis position="bottom" axisId="x-axis-id" />
        <ChartsYAxis position="left" axisId="y-axis-id" />
        <ChartsLegend />
    </ResponsiveChartContainer>)
}

// lays out the charts in a column
function Charts() {
    const chunkedChartData = useSelector(state => state.charts.chunkedChartData);
    const chartSettings = useSelector(state => state.chartSettings.chartSettings);
    const [expandedChunks, setExpandedChunks] = useState(() => {
        const initial = {};
        if (Object.keys(chunkedChartData).length > 0) {
            chunkedChartData?.chunks.forEach((_, index) => {
                initial[index] = true;
            });
        }
        return initial;
    });

    const toggleChunk = (index) => {
        setExpandedChunks(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    // Calculate global min/max elevation if uniform scale is enabled
    let globalElevationRange = null;
    if (chartSettings.uniformElevationScale && chunkedChartData?.chunks) {
        const allElevations = chunkedChartData.chunks.flatMap(chunk => chunk.e);
        let min = Infinity, max = -Infinity;
        for (const e of allElevations) {
            if (e < min) min = e;
            if (e > max) max = e;
        }
        globalElevationRange = { min, max };
    }

    // Updated styles for responsive layout
    const page_div_styles = {
        display: "flex",
        flexDirection: "column",
        width: '100%',
        padding: "2px",
        alignItems: "center",
        gap: "0.1in",
    }

    const chart_div_styles = {
        display: "flex",
        flexDirection: "column",
        width: chartSettings.sideBySideLayout ? 'calc(50% - 0.05in)' : '100%',
        height: `${chartSettings.chartHeight}in`,
        minHeight: `${chartSettings.chartHeight}in`,
        flex: "0 0 auto",
        overflow: "visible",
        pointerEvents: "none"
    }

    const chart_content_styles = {
        display: "flex",
        flex: 1,
        width: "100%",
        height: "100%",
        minHeight: `${chartSettings.chartHeight}in`,
        overflow: "visible",
        pointerEvents: "none"
    }

    const poi_container_styles = {
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        width: chartSettings.sideBySideLayout ? 'calc(50% - 0.05in)' : '100%',
        height: 'auto',
        minHeight: chartSettings.sideBySideLayout ? `${chartSettings.chartHeight}in` : `${chartSettings.chartHeight * 0.6}in`,
        flex: "0 0 auto",
        overflow: "visible",
        pointerEvents: "none"
    }

    const card_row_div_styles = {
        display: "flex",
        flexDirection: "column",
        width: "100%",
        flex: "0 0 auto",
        padding: "0",
        marginBottom: "0.1in",
        gap: "0.1in",
        justifyContent: "center",
        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
        overflow: "visible",
        pointerEvents: "none"
    }

    const content_container_styles = {
        display: "flex",
        flexDirection: chartSettings.sideBySideLayout ? "row" : "column",
        width: "100%",
        gap: "0.1in",
        alignItems: "flex-start",
        justifyContent: "space-between"
    }

    return (
        <div className="chart-container print-overflow-auto print-all-charts" style={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            padding: "0",
            alignItems: "center",
            width: "100%",
            height: "100%",
            position: "relative",
            // overflow: "hidden"
        }}>
            <Box sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                p: 0.5,
                bgcolor: 'background.paper',
                zIndex: 1
            }}>
            </Box>
            <Box className="print-overflow-auto print-all-charts" sx={{
                width: '100%',
                maxWidth: { xs: '100%', sm: '800px' },
                mx: 'auto',
                height: '100%',
                // overflow: 'hidden',
                boxSizing: 'border-box'
            }}>
                <div className="print-overflow-auto print-all-charts" style={{
                    ...page_div_styles,
                    height: "calc(100% - 50px)",
                    width: "100%",
                    overflow: "auto",
                    WebkitOverflowScrolling: "touch",
                    touchAction: "pan-y"
                }}>
                    {chunkedChartData && chunkedChartData.chunks?.map((chunk, chunk_index) => (
                        <div style={{
                            ...card_row_div_styles,
                            borderBottom: chunk_index === chunkedChartData.chunks.length - 1 ? "none" : "1px solid rgba(0, 0, 0, 0.2)",
                            marginBottom: chunk_index === chunkedChartData.chunks.length - 1 ? "0" : "0.5rem",
                            paddingBottom: chunk_index === chunkedChartData.chunks.length - 1 ? "0" : "0.5rem",
                            position: 'relative'
                        }} key={chunk_index}>
                            <Box sx={{
                                width: '100%',
                                borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                                bgcolor: 'background.paper',
                                mb: 0.5,
                                pointerEvents: "auto",
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}>
                                {(() => {
                                    const minDist = Math.min(...chunk.d);
                                    const maxDist = Math.max(...chunk.d);
                                    const chunkLength = maxDist - minDist;
                                    return (
                                        <>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="subtitle1" sx={{ px: 0.5, py: 0.5 }}>
                                                    {chartSettings.distUnit.charAt(0).toUpperCase() + chartSettings.distUnit.slice(1) + 's'}: {minDist.toFixed(1)} - {maxDist.toFixed(1)}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        ↑{chunk.elevationGain || 0}{chartSettings.elevAbbr}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        ↓{chunk.elevationLoss || 0}{chartSettings.elevAbbr}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        →{chunkLength > 0 ?
                                                            `${chunkLength.toFixed(1)} ${chartSettings.distAbbr}` :
                                                            `${chartSettings.distAbbr}`
                                                        }
                                                    </Typography>
                                                </Box>
                                                {chunk.userInChunk && chartSettings.showUserLocation && (
                                                    <Tooltip
                                                        title="You are currently in this section of the route"
                                                        arrow
                                                        enterTouchDelay={0}
                                                        leaveTouchDelay={3000}
                                                    >
                                                        <IconButton
                                                            size="small"
                                                            sx={{
                                                                color: '#1976d2',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                                                }
                                                            }}
                                                        >
                                                            <PersonIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                            <IconButton onClick={() => toggleChunk(chunk_index)} size="small">
                                                {expandedChunks[chunk_index] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            </IconButton>
                                        </>
                                    );
                                })()}
                            </Box>
                            <Collapse in={expandedChunks[chunk_index] !== false} sx={{ width: '100%' }}>
                                <div style={content_container_styles}>
                                    <div className="print-chart-height" style={chart_div_styles}>
                                        <div className="print-chart-height" style={chart_content_styles}>
                                            <Chart
                                                chunk={chunk}
                                                yAxisRange={globalElevationRange}
                                            />
                                        </div>
                                    </div>
                                    <div style={poi_container_styles}>
                                        <Typography variant="caption" color="text.secondary" sx={{ px: 0.5, pt: 0.5, pointerEvents: "auto" }}>
                                            * indicates POI is more than {chartSettings.offRouteThreshold} {chartSettings.distUnit}s from route
                                        </Typography>
                                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 0.5, overflow: 'auto', pointerEvents: "auto" }}>
                                            {chunk.poi
                                                .filter((poi) => {
                                                    if (chartSettings.hideOffRoutePoi && poi.offRouteDistance > chartSettings.offRouteThreshold) {
                                                        return false;
                                                    }
                                                    return true;
                                                })
                                                .map((poi, index, filteredPois) => {
                                                    const nextPoi = filteredPois[index + 1];
                                                    const distanceToNext = nextPoi ? (nextPoi.d - poi.d) : null;
                                                    return (
                                                        <Box key={index} className="poi-item" sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.25, fontSize: `${chartSettings.poiFontSize}rem` }}>
                                                            <PoiIcon type={poi.t} />
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                                                <span>{Math.round(poi.d)} {chartSettings.distAbbr}. - {poi.n}{poi.offRouteDistance > chartSettings.offRouteThreshold ? `* (~${poi.offRouteDistance.toFixed(1)} ${chartSettings.distAbbr})` : ''}{chartSettings.showDistanceToNextPoi && distanceToNext !== null ? ` (${distanceToNext.toFixed(1)} ${chartSettings.distAbbr} to next)` : ''}</span>
                                                                {chartSettings.showPoiDescriptions && poi.description && (
                                                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 0.25 }}>
                                                                        {poi.description}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    );
                                                })}
                                        </Box>
                                    </div>
                                </div>
                            </Collapse>
                        </div>
                    ))}
                    <br />
                </div>
            </Box>
        </div>
    );
}

export default Charts;